import { FeatureFlag, ResolverTag } from '@ps/web-core-enums';
import type { TranslationTag } from '@ps/web-core-intl';
import { type Dispatch, useFeature } from '@ps/web-core-store';
import { StarsButton, Translation } from '@ps/web-shared-components';
import { translatedLink } from '@ps/web-shared-ui';
import type { SyntheticEvent } from 'react';
import React, { useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import useWizardContext from '@swf/core/hooks/useWizardContext';
import useWizardExit from '@swf/core/hooks/useWizardExit';
import { select } from '@swf/core/store';

import { CashierFlowType } from '../../../../types/Cashier';
import Container from '../../../Container/Container';
import Prompt from '../../../Prompt/Prompt';

import {
  TXTCLI_Check_your_Email,
  TXTWCL_An_email_has_been_sent_to_N0___ELL,
  TXTWCL_Before_you_can_make_a_depositX_ELL_2,
  TXTWCL_Before_you_can_make_a_withdraw_ELL_1,
  TXTWCL_Email_verification,
  TXTWCL_Enter_code_from_email,
  TXTWCL_Help_with_deposits,
  TXTWCL_Help_with_withdrawals,
  TXTWCL_Hi_N0X_please_verify_your_emai_ELL,
  TXTWCL_Just_so_you_know,
  TXTWCL_Return_home,
  TXTWCL_Send_verification_email,
  TXTWCL_You_can_change_your_email_addr_ELL
} from './EmailVerification.messages.json';
import { buttonsWrapper, emailVerificationWrapper } from './EmailVerification.scss';
import HelpSectionContent from './components/HelpSectionContent';

const getTranslations = (cashierFlow: CashierFlowType) => {
  const translationMapping: Partial<
    Record<CashierFlowType, { helpTitle: TranslationTag; text: TranslationTag }>
  > = {
    [CashierFlowType.FirstWithdraw]: {
      helpTitle: TXTWCL_Help_with_withdrawals,
      text: TXTWCL_Before_you_can_make_a_withdraw_ELL_1
    },
    [CashierFlowType.FirstP2pTransfer]: {
      helpTitle: TXTWCL_Just_so_you_know,
      text: TXTWCL_Before_you_can_make_a_withdraw_ELL_1
    },
    [CashierFlowType.FirstDeposit]: {
      helpTitle: TXTWCL_Help_with_deposits,
      text: TXTWCL_Before_you_can_make_a_depositX_ELL_2
    }
  };

  return (
    translationMapping[cashierFlow] || {
      helpTitle: TXTWCL_Help_with_deposits,
      text: TXTWCL_Before_you_can_make_a_depositX_ELL_2
    }
  );
};

const EmailVerification: React.FC = () => {
  const {
    modal,
    account: { sendValidationEmail },
    localSettings
  } = useDispatch<Dispatch>();

  const {
    data: { cashierFlow }
  } = useWizardContext();

  const firstName = useSelector(select.account.getFirstName);
  const emailAddress = useSelector(select.account.getEmail);
  const hideEmailCode = useFeature(FeatureFlag.HideEmailConfirmationCode);
  const isEmbedded = useSelector(select.device.isEmbedded);
  const [promptVisible, setPromptVisible] = useState(false);
  const exitWizard = useWizardExit();
  const { email } = useSelector(select.account.get);

  const viewEmail = (e: SyntheticEvent) => {
    e.preventDefault();
    isEmbedded
      ? window.starsweb?.app?.execute?.(`account/details/email`)
      : modal.replace([{ path: '/account/details/email' }]);
  };

  const sendVerificationEmail = useCallback(() => {
    if (!hideEmailCode) {
      localSettings.set({ cashier: { emailVerificationDateTime: new Date().toString() } });
    }
    sendValidationEmail();
    setPromptVisible(true);
  }, [hideEmailCode, sendValidationEmail, localSettings]);

  const handleClose = () => {
    setPromptVisible(false);
    exitWizard();
  };

  return (
    <Container
      page={EmailVerification}
      headerProps={{
        title: <Translation {...TXTWCL_Email_verification} />,
        buttons: { back: true, exitWizardOnBackClick: true }
      }}
      helpSupportResolver={ResolverTag.HelpUpdateDetails}
      helpSection={{
        title: getTranslations(cashierFlow).helpTitle,
        body: <HelpSectionContent />
      }}
    >
      {promptVisible && (
        <Prompt
          variant="default"
          onClose={() => setPromptVisible(false)}
          onPrimaryButtonClick={handleClose}
          title={{ translation: TXTCLI_Check_your_Email }}
          primaryButton={{ translation: TXTWCL_Return_home }}
        >
          <Translation
            tagName="p"
            values={{ 0: email }}
            {...TXTWCL_An_email_has_been_sent_to_N0___ELL}
          />
        </Prompt>
      )}
      <div className={emailVerificationWrapper}>
        <Translation
          tagName="h2"
          {...TXTWCL_Hi_N0X_please_verify_your_emai_ELL}
          values={{ 0: firstName }}
        />
        <Translation tagName="p" {...getTranslations(cashierFlow).text} />
        {!!emailAddress && (
          <p>
            <strong>{emailAddress}</strong>
          </p>
        )}
        <Translation
          tagName="p"
          {...TXTWCL_You_can_change_your_email_addr_ELL}
          values={{
            a: translatedLink({
              href: '/account/details/email',
              onClick: viewEmail
            })
          }}
        />
        <div className={buttonsWrapper}>
          <StarsButton
            translation={TXTWCL_Send_verification_email}
            onClick={sendVerificationEmail}
            type={hideEmailCode ? 'button' : 'submit'}
          />

          {!hideEmailCode && (
            <StarsButton
              variant="secondary"
              translation={TXTWCL_Enter_code_from_email}
              type="submit"
            />
          )}
        </div>
      </div>
    </Container>
  );
};

EmailVerification.displayName = 'EmailVerification';

export default EmailVerification;
